import { useCallback, useEffect, useMemo, useState } from 'react';
import { customProviderItemsFilter, customGameTypesFilter } from 'features/creativeFunctionality/data';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from 'features/creativeFunctionality/redux';
import { isEqualByKeys, reorderInsideType } from '../shared';
import convertFilter from './convertFilter';

const useCustomProvidersModel = () => {
  const dispatch = useDispatch();
  const { providers, previousProviders } = useSelector(state => state.creativeFunctionality, shallowEqual);

  const [activeProviders, changeActiveProviders] = useState(customProviderItemsFilter);
  const [activeGameTypes, changeActiveGameTypes] = useState(customGameTypesFilter);

  const returnBackDisabled = useMemo(
    () => isEqualByKeys(previousProviders?.items ?? [], activeProviders, ['name', 'isActive']),
    [activeProviders, previousProviders],
  );

  const activeGameType = useMemo(() => activeGameTypes.find(gt => gt.isActive), [activeGameTypes]);

  const handleActiveGameTypes = useCallback(gameType => {
    changeActiveGameTypes(prev => prev.map(gt => ({ ...gt, isActive: gt.value === gameType.value })));
  }, []);

  const handleActiveProviders = useCallback(provider => {
    if (provider.disabled) return;
    changeActiveProviders(prev => {
      const providerApiValue = (provider.apiValue ?? provider.value).toString().toLowerCase();

      return prev.map(p =>
        (p.apiValue ?? p.value).toString().toLowerCase() === providerApiValue
          ? { ...p, isActive: !provider.isActive }
          : p,
      );
    });
  }, []);

  const changeProviderOrder = useCallback((provider, direction) => {
    if (provider.disabled) return;
    changeActiveProviders(prev => {
      const typedItems = prev.filter(p => p.type === provider.type);
      const idx = typedItems.findIndex(p => p.value === provider.value);
      if (idx === -1) return prev;
      const newIdx = direction > 0 ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= typedItems.length) return prev;
      return reorderInsideType(prev, provider.type, idx, newIdx);
    });
  }, []);

  const changeProviderOrderByDragging = useCallback(({ startIndex, endIndex, type }) => {
    if (startIndex < 0 || endIndex < 0 || startIndex === endIndex) return;
    changeActiveProviders(prev => {
      return reorderInsideType(prev, type, startIndex, endIndex);
    });
  }, []);

  const handleSendConfirm = useCallback(() => {
    dispatch(actions.setCustomProviders(convertFilter(activeProviders), activeProviders));
  }, [dispatch, activeProviders]);

  const handleReturnBack = useCallback(() => {
    if (previousProviders?.items) {
      changeActiveProviders(previousProviders.items);
    }
  }, [previousProviders]);

  useEffect(() => {
    dispatch(actions.getCustomProviders());
  }, [dispatch]);

  useEffect(() => {
    if (providers?.count > 0) {
      changeActiveProviders(providers.items);
    }
  }, [providers]);

  return {
    input: {
      handleActiveProviders,
      changeProviderOrder,
      changeProviderOrderByDragging,
      handleActiveGameTypes,
      activeProviders,
      activeGameTypes,
      handleSendConfirm,
      handleReturnBack,
    },
    output: { activeGameType, returnBackDisabled },
  };
};

export { useCustomProvidersModel };
